import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

const FALLBACK_DOC_TYPES = [
  'Drawings', 'Method Statement', 'Risk Assessment', 'Programme', 'Contract',
  'H&S Plan', 'Insurance', 'Correspondence', 'Photo', 'Other',
] as const;

interface Props {
  onClose: () => void;
  projectId: string;
  projectName: string;
}

export function AddDocModal({ onClose, projectId, projectName }: Props) {
  const qc = useQueryClient();
  const { values: docTypes } = useReferenceList('doc-types', FALLBACK_DOC_TYPES);
  const [name, setName] = useState('');
  const [type, setType] = useState('Drawings');
  const [notes, setNotes] = useState('');
  const [ref, setRef] = useState('');
  const [date, setDate] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      let fileUrl: string | null = null;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post<{ url: string }>('/uploads/file', fd);
        fileUrl = up.data.url;
      }
      return api.post(`/projects/${projectId}/documents`, {
        name: name.trim(),
        type,
        notes: notes.trim() || null,
        ref: ref.trim() || null,
        date: date || null,
        url: fileUrl,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Document added', name);
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">📄 Add Project Document</div>
        <div className="modal-sub" id="doc-proj-name">{projectName}</div>
        <div className="form-row"><label className="form-label">Document Name</label><input type="text" className="form-input" id="doc-name" placeholder="e.g. Drawings Issue 3" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">Document Type</label>
          <SearchableSelect
            id="doc-type"
            value={type}
            onChange={setType}
            placeholder="— Select type —"
            options={docTypes.map((t) => ({ value: t, label: t }))}
          />
        </div>
        <div className="form-row"><label className="form-label">Description / Notes</label><textarea className="form-textarea" id="doc-notes" placeholder="Brief description of the document…" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="form-row"><label className="form-label">Reference Number (optional)</label><input type="text" className="form-input" id="doc-ref" placeholder="e.g. DWG-001-Rev3" value={ref} onChange={(e) => setRef(e.target.value)} /></div>
        <div className="form-row"><label className="form-label">Date</label><input type="date" className="form-input" id="doc-date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">Attach File (optional — drag &amp; drop or click)</label>
          <div
            className="drop-zone"
            id="doc-dropzone"
            style={{ padding: 12, borderRadius: 8, border: '2px dashed var(--border)', textAlign: 'center', cursor: 'pointer' }}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
            onClick={() => document.getElementById('doc-file-input')?.click()}
          >
            <div style={{ fontSize: 18, marginBottom: 4 }}>📂</div>
            <div style={{ fontSize: 11 }}>{file ? `📎 ${file.name}` : 'Drop contract/document file here or click to attach'}</div>
            <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 2 }}>PDF, Word, JPG, PNG</div>
            <input type="file" id="doc-file-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !name.trim()}>{save.isPending ? 'Saving…' : 'Save Document'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
